/* eslint-disable import/no-cycle */
import { Delete } from '@ce-cfbdigital/cgl-cfb-fe-foundation';
import DOMPurify from 'dompurify';
import PubSub from 'pubsub-js';

import oktaAuth from '../auth/oktaAuth';
import { INLINE_TRANSLATIONS } from '../constants';
import { ACCOUNT_AND_REGION, SELECTED_ACCOUNT, SELECTED_REGION } from '../Header/constants';
import { logger } from '../logger';
import { getAppConfig, getServiceConfig } from '.';
import { Get } from './api-wrapper';
import { checkForNotificationPermission } from './auth';
import { ACCOUNT, BASED_URL_KEY, BULK, CCCNA_SALES_AREAS, CURRENT_ACCOUNT, FIBI, REGION } from './constants';

function waitFor(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

const falsyReturn = () => null;

const debounce = (fn, wait, immediate) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      if (!immediate) fn.apply(this, [...args]);
    }, wait);
    if (immediate && !timeout) fn.apply(this, [...args]);
  };
};

const throttle = (func, limit) => {
  let inThrottle;
  return (...args) => {
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  };
};

/**
 * Check if its mobile device or not
 * @returns {boolean} empty or not
 */
/* eslint-enable */
const isMobileDevice = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
const srcSetToArray = data => {
  const srcArr = Object.entries(data);
  return srcArr.map(item => `${item[1]} ${item[0]}`);
};

/**
 * Check if item is empty object or not
 * @param {array} objContent Data Object
 * @returns {boolean} empty or not
 */
const isEmpty = objContent => Object.keys(objContent).length === 0 && objContent.constructor === Object;

let intervalId;
const scrollBelow = wrapper => {
  const scrollDifference = window.outerHeight * 0.3; // 30% of Window
  const lastKnownScrollPosition = window.pageYOffset + window.innerHeight;
  return wrapper && lastKnownScrollPosition > wrapper.clientHeight - scrollDifference;
};

const scrollBy = (scrollHeight, step) => {
  const steps = Math.floor(scrollHeight / step);
  let currentStep = Math.floor(window.pageYOffset / step);
  let currentOfset = window.pageYOffset;
  const scrollingEvent = setInterval(() => {
    if (currentStep === steps) clearInterval(scrollingEvent);

    if (currentStep >= steps) {
      currentOfset -= step;
      window.scrollTo(0, currentOfset);
      currentStep -= 1;
    } else {
      currentOfset += step;
      window.scrollTo(0, currentOfset);
      currentStep += 1;
    }
  }, step);
};

const scrollStep = scrollStepInPx => {
  if (window.pageYOffset === 0) {
    clearInterval(intervalId);
  }
  window.scroll(0, window.pageYOffset - scrollStepInPx);
};

const scrollToTop = (scrollStepInPx = 100, delay = 0) => {
  intervalId = setInterval(scrollStep.bind(this, scrollStepInPx), delay);
};

const scrollToTopAppContent = () => {
  const appContent = document.querySelector('.app-content');
  if (appContent) {
    appContent.scrollTop = 0;
  }
};

/**
 * @method isScrollToBelow
 * @description Check if page scroll till wrapper last
 * @param {*} wrapper reff of the container to get this function invoked when user reach to end of this wrapper
 * @returns {boolean} Return true if page react wrapper last
 */
const isScrollToBelow = wrapper => {
  const scrollDifference = window.outerHeight * 0.3; // 30% of Window
  const lastKnownScrollPosition = window.pageYOffset + window.innerHeight;
  return wrapper && lastKnownScrollPosition > wrapper.clientHeight - scrollDifference;
};

const getBaseUrl = () => document.location.origin;

/**
 * return options with value key
 * @param {array} selectbox option Object
 * @returns {array}
 */
const selectBoxOptions = options =>
  options.map(item => ({
    ...item,
    value: item.id
  }));

const convertArrToObj = (arr, key) => {
  if (!key) return null;
  const obj = {};
  for (let i = 0; i < arr.length; i += 1) {
    obj[arr[i][key]] = arr[i];
  }
  return obj;
};

const storageAPI = (storageType = 'sessionStorage') => ({
  /**
   * setItem
   * @param {string} key sessionStorage key name
   * @param {*} value data to be stored in the key
   */
  setItem: (key, value) => window[storageType].setItem(key, JSON.stringify(value)),
  /**
   * setItem
   * @param {string} key sessionStorage key name
   */
  getItem: key => {
    const data = window[storageType].getItem(key);

    return data ? JSON.parse(data) : '';
  },
  /**
   * setItem
   * @param {string} key sessionStorage key name
   */
  removeItem: key => window[storageType].removeItem(key),
  clear: () => window[storageType].clear()
});

/**
 * update sessionStorage
 */
const updateSessionStorage = storageAPI('sessionStorage');
const updateLocalStorage = storageAPI('localStorage');

/**
 * blobToPdf function return the desired pdf file for the blob response which supports all the browser.
 * @param    {[Object]} response recieved in ajax call
 * @param    {[String]} filename for the pdf file.
 * @return   {[File]} returns pdf file
 */
const blobToFile = (data, filename = 'filename.pdf', mime = 'application/pdf') => {
  const blob = new Blob([data], {
    type: mime || 'application/octet-stream'
  });
  if (typeof window.navigator.msSaveBlob !== 'undefined') {
    window.navigator.msSaveBlob(blob, filename);
  } else {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = DOMPurify.sanitize(url);

    // Safari thinks _blank anchor are pop ups. We only want to set _blank
    // target if the browser does not support the HTML5 download attribute.
    // This allows you to download files in desktop safari if pop up blocking
    // is enabled.
    if (typeof link.download !== 'undefined') {
      link.setAttribute('download', filename);
    }

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Return current logged-in user email id
 * @param {object} Okta Auth instance or Okta user object
 * @returns {string} User email id
 */
const getEmail = async auth => {
  const user = typeof auth.email === 'string' ? auth : await auth.getUser();
  return user.email || null;
};

const validateDomain = redirectUrl => {
  const allowedDomains = [
    'localhost:3000',
    'food.dev.cglcloud.in',
    'mycargill.dev.cglcloud.in',
    'stage.mycargill.com',
    'www.mycargill.com'
  ];
  const parsedUrl = new URL(redirectUrl, window.location.origin);
  return allowedDomains.includes(parsedUrl.host);
};

const redirectTo = url => {
  const sanitiseUrl = DOMPurify.sanitize(url);
  const isValidDomain = validateDomain(sanitiseUrl);
  if (isValidDomain) {
    window.history.pushState({}, '', sanitiseUrl);
    window.location.reload();
  }
};

/**
 * Return current logged-in user email id
 * @param {object} Okta Auth instance or Okta user object
 * @returns {string} Users name
 */
const getUsername = async auth => {
  const user = typeof auth.name === 'string' ? auth : await auth.getUser();
  return user.name || null;
};

/**
 * return options with value key
 * @param {string} userMail user email address
 * @returns {object} userData
 */
const getUserInfo = (originalUri = '') => {
  const path = window.location.pathname;
  const { url } = getServiceConfig('getUserDetails');
  return Get(url)
    .then(response => {
      const {
        data: { applications }
      } = response;
      const { tandC, url: baseURL = '' } = applications.find(item => item.appRefName.toLowerCase() === FIBI);
      let baseUrlFromSettings = `/${baseURL}`;

      if (!updateSessionStorage.getItem('tandC')) {
        updateSessionStorage.setItem('tandC', tandC);
      }

      if (!path.includes('/food/onboarding')) {
        if (originalUri) {
          const page = originalUri.split('/pages/').pop();
          const countryLangURL = baseUrlFromSettings.split('/pages')[0];
          baseUrlFromSettings = DOMPurify.sanitize(`${countryLangURL}/pages/${page}${window.location.search}`);
        }

        const builtPath = path.includes('food') ? baseUrlFromSettings : baseUrlFromSettings.replace('food/', '');
        sessionStorage.setItem(BASED_URL_KEY, builtPath.split('/pages')[0]);
        return { ...response, baseURL: builtPath };
      }
      return response;
    })
    .catch(error => logger.error('An error occured while getting the user details', error));
};

const deleteRedcarpetCookies = () => {
  const cookies = document.cookie.split(';');
  cookies.forEach(cookie => {
    const [name] = cookie.split('=').map(c => c.trim());
    if (name.includes('cfbRedcarpet-token-storage')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
};

const handleLogout = async () => {
  const appConfig = getServiceConfig('deleteSession');
  const path = updateSessionStorage.getItem('fibiPath');
  const host = updateSessionStorage.getItem('fibiHost') || window.location.origin;
  updateLocalStorage.removeItem(BASED_URL_KEY);
  const { entry } = getAppConfig('login');
  const postLogoutRedirectUri = `${host}${path}${entry}`;

  try {
    await Delete(appConfig, oktaAuth);
    await oktaAuth.signOut({
      revokeAccessToken: false,
      postLogoutRedirectUri
    });
  } catch (e) {
    logger.error('Error logging out user', e);
  }
};

const getUserInfoFromOkta = async () => {
  try {
    return await oktaAuth.getUser();
  } catch (err) {
    handleLogout();
    return err;
  }
};

const getCurrentDate = () => new Date();

const getCachedUserOktaInfo = (() => {
  let userInfo;
  return async () => {
    if (!userInfo) {
      userInfo = getUserInfoFromOkta();
    }
    return userInfo;
  };
})();

const accountAndContextFaillureMessage = () => {
  const { lang = 'en' } = getAppConfig('locale');
  const translations = getAppConfig(INLINE_TRANSLATIONS);
  return translations[lang]?.accountContextMsg;
};

const getSelectedRegion = selectedRegion => (selectedRegion ? `?region=${selectedRegion}` : '');

const sortAlphabetically = list => (list.length ? list.sort((a, b) => (a.fullname > b.fullname ? 1 : -1)) : list);

const getAccountAndRegion = (() => {
  let accountAndRegion = '';
  let accountsCounter = 0;
  return async (selectedRegion = null, override = false) => {
    if ((!accountAndRegion && !accountsCounter) || override) {
      accountsCounter += 1;
      const { url } = getServiceConfig('getAccountsAndRegions');
      try {
        const accountsData = await Get(`${url}${getSelectedRegion(selectedRegion)}`);
        updateSessionStorage.setItem('isAccountAssociated', true);
        const isAccounts = accountsData && accountsData.data;
        const accounts = (isAccounts && accountsData.data.accounts) || [];
        const regions = (isAccounts && accountsData.data.regions) || [];
        const customerTypes = (isAccounts && accountsData.data.customerTypes) || [];
        accountAndRegion = {
          accounts: sortAlphabetically(accounts),
          regions: sortAlphabetically(regions),
          customerTypes
        };
        if (accountsData) {
          updateSessionStorage.removeItem('ctaAccount');
          updateSessionStorage.removeItem('ctaRegion');
        }
      } catch (err) {
        if (err?.error?.responseError?.statusCode === 'A101') {
          updateSessionStorage.setItem('isAccountAssociated', false);
          PubSub.publish('xhrError', {
            text: ''
          });
        } else {
          PubSub.publish('xhrError', {
            text: accountAndContextFaillureMessage()
          });
        }
        logger.error('Error in getting accounts', err);
        accountAndRegion = { accounts: [], regions: [] };
      }
    }
    if (accountAndRegion) return accountAndRegion;
    // because the getAccountAndRegion can be invoked from a different sources (possiblly, even at the same time)
    // and it shouldn't be the case that the subsequent calls have been sent with no result
    // therfore the waitFor method - by the time of its execution - we will have the result from XHR request.
    await waitFor(100);
    accountAndRegion = await getAccountAndRegion(selectedRegion);
    return accountAndRegion;
  };
})();

const doesTheSearchParamContainCTAInfo = () => {
  const captureSearchParamFromUrl = window.location.search || '';
  if (captureSearchParamFromUrl.length && captureSearchParamFromUrl.includes(CURRENT_ACCOUNT)) {
    return captureSearchParamFromUrl;
  }
  return '';
};

const checkIfUrlInlcudesCtaParameteres = () => {
  const captureSearchParamFromUrl = doesTheSearchParamContainCTAInfo();
  if (captureSearchParamFromUrl.length) {
    const splitSearchParams = captureSearchParamFromUrl.split('&');
    const getCurrentAccount = splitSearchParams.find(item => item.includes(CURRENT_ACCOUNT));
    const currentAccountDetails = getCurrentAccount.split('=').filter(item => item);
    const encodedAccountAndRegion = currentAccountDetails[currentAccountDetails.length - 1];
    const decodedAccountAndRegion = atob(encodedAccountAndRegion).split('&');
    const getAccount = decodedAccountAndRegion
      .find(item => item.includes(ACCOUNT))
      .split('=')
      .find(item => item !== ACCOUNT);
    const getRegion = decodedAccountAndRegion
      .find(item => item.includes(REGION))
      .split('=')
      .find(item => item !== REGION);
    if (getAccount) {
      updateSessionStorage.setItem('ctaAccount', getAccount);
    }
    if (getRegion) {
      updateSessionStorage.setItem('ctaRegion', getRegion);
    }
  }
};

/**
 * setAccountAndRegionSelection holds the logic to account and region selection
 * @param {accountOrRegion} array gives a list of account or region
 * @param {slectedAccountOrRegion} string defines which account or region is to be selected
 * @param {sessionConstant} string defines whether the key is account/region
 */

const setAccountAndRegionSelection = (accountOrRegion = [], slectedAccountOrRegion = '', sessionConstant = '') => {
  const isValid = accountOrRegion.length && accountOrRegion.find(item => item.shortname === slectedAccountOrRegion);
  return accountOrRegion.map(item => {
    if (
      accountOrRegion.length === 1 ||
      (isValid && item.shortname === slectedAccountOrRegion) ||
      (!isValid && item.selected)
    ) {
      const selectedAccountOrRegion = { ...item, selected: true };
      updateSessionStorage.setItem(sessionConstant, selectedAccountOrRegion);
      return selectedAccountOrRegion;
    }
    return { ...item, selected: false };
  });
};

// getDefaultAccounts is responsible to get the default account's list in case there are no accounts for the selected region
// it also restricts the api call to be called for a single time.
const getDefaultAccounts = (() => {
  let data = '';
  let counter = 0;
  return async () => {
    if (!data && !counter) {
      counter += 1;
      data = await getAccountAndRegion('', true);
    }
    if (data) return data;
    await waitFor(100);
    data = await getDefaultAccounts();
    return data;
  };
})();

/**
 * is responsible to decide with which query param the account api should be called - region -
 * is either selected from session ('selectedRegion' or 'ctaRegion') if session doesn't hold
 * any region information it then fetches the account api with no query param
 *
 * if the account list is empty:
 * a second call with no region query param is initiated
 *
 * if the account list isn't empty and the selected account (from the session) does exist in the payload:
 * The account that was stored in the session will be considered.
 *
 * if the account list isn't empty and the selected account (from the session) doesn't exist in the payload:
 * Which ever account that is defaulted to true from the paylaod will be considered as the selcted account.
 */

const cacheAccountAndRegion = async () => {
  const { shortname: selectedRegion = '' } = updateSessionStorage.getItem(SELECTED_REGION);
  const { shortname: selectedAccount = '' } = updateSessionStorage.getItem(SELECTED_ACCOUNT);
  const ctaRegion = updateSessionStorage.getItem('ctaRegion');
  const ctaAccount = updateSessionStorage.getItem('ctaAccount');
  // checkIfCtaRegionExists checks if there is region related information in the session
  const checkIfCtaRegionExists = ctaRegion || selectedRegion;
  const data = await getAccountAndRegion(checkIfCtaRegionExists);
  const isAccountsEmptyForSelectedRegion = !(
    data.accounts &&
    data.accounts.length &&
    data.accounts.some(acc => acc.selected)
  );
  // if the account list is empty or none of the account is selected as true then make a second call to accounts api
  // with no query params
  const { accounts, regions, customerTypes } = isAccountsEmptyForSelectedRegion ? await getDefaultAccounts() : data;
  // checkIfCtaAccountExists checks if there is account related information in the session
  const checkIfCtaAccountExists =
    ctaAccount && accounts.length ? accounts.find(item => item.globalSfid === ctaAccount).shortname : selectedAccount;
  const accAndReg = {
    accounts: setAccountAndRegionSelection(accounts, checkIfCtaAccountExists, SELECTED_ACCOUNT),
    regions: setAccountAndRegionSelection(regions, checkIfCtaRegionExists, SELECTED_REGION),
    customerTypes
  };
  updateSessionStorage.setItem(ACCOUNT_AND_REGION, accAndReg);
  return accAndReg;
};

const getCachedUserContext = (() => {
  let userContext;
  let counter = 0;
  return async () => {
    if (!userContext) {
      counter += 1;
      const { url } = getServiceConfig('getUserContext');
      const { path } = getAppConfig('server');

      if (counter === 1) {
        try {
          userContext = await Get(url, true);
          updateSessionStorage.setItem('isContextFailure', false);
        } catch (err) {
          const {
            error: { responseError }
          } = err;
          updateSessionStorage.setItem('isContextFailure', true);

          if (responseError?.statusCode === 'A101') {
            PubSub.publish('xhrError', {
              text: ''
            });
          }

          if (responseError?.statusCode === 'F101') {
            PubSub.publish('xhrError', {
              text: ''
            });
            if (!window.location.pathname.includes('errorpage')) {
              window.location.pathname = `${path}/errorpage`;
            }
          }
          logger.error('Error in getting context', err);
          userContext = {};
        }
        counter = 0;
      }
    }
    if (userContext) {
      return userContext;
    }
    await waitFor(100);
    userContext = await getCachedUserContext();
    return userContext;
  };
})();

const getCachedUserInfo = (() => {
  let infoUser;
  return async () => {
    if (!infoUser) {
      infoUser = getUserInfo();
    }
    return infoUser;
  };
})();

const translate = (valueToBeTranslated = '', translationOptions = []) => {
  if (valueToBeTranslated && translationOptions.length) {
    const translatedObj = translationOptions.find(
      item => item.value.toLowerCase() === valueToBeTranslated.toLowerCase()
    );
    return translatedObj ? translatedObj.label : valueToBeTranslated;
  }
  return valueToBeTranslated;
};

/**
 *
 * @param {string} salesAreaToVerify
 * @description verify if the sales area is in the CCC NA codes
 * @returns {boolean}
 */
const isCCCNA = salesAreaToVerify => CCCNA_SALES_AREAS.includes(salesAreaToVerify);

const PHONE_REGEX = /([+]?\d{1,2}[.-\s]?)?(\d{3}[.-]?){1}\d{2}/g;

const isValidPhoneFormat = value => {
  const regex = new RegExp(PHONE_REGEX);
  return regex.test(value);
};
/**
 * detect IE
 * returns version of IE or false, if browser is not Internet Explorer
 */
const detectIE = () => {
  const ua = window.navigator.userAgent;

  // Test values; Uncomment to check result …

  // IE 10
  // ua = 'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.2; Trident/6.0)';

  // IE 11
  // ua = 'Mozilla/5.0 (Windows NT 6.3; Trident/7.0; rv:11.0) like Gecko';

  // Edge 12 (Spartan)
  // ua = 'Mozilla/5.0 (Windows NT 10.0; WOW64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/39.0.2171.71 Safari/537.36 Edge/12.0';

  // Edge 13
  // ua = 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/46.0.2486.0 Safari/537.36 Edge/13.10586';

  const msie = ua.indexOf('MSIE ');
  if (msie > 0) {
    // IE 10 or older => return version number
    return parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10);
  }

  const trident = ua.indexOf('Trident/');
  if (trident > 0) {
    // IE 11 => return version number
    const rv = ua.indexOf('rv:');
    return parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10);
  }

  const edge = ua.indexOf('Edge/');
  if (edge > 0) {
    // Edge (IE 12+) => return version number
    return parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10);
  }

  // other browser
  return falsyReturn();
};

const getFileExtension = mimeType => {
  let extn;
  switch (mimeType) {
    case 'image/jpeg':
      extn = 'jpg';
      break;
    case 'image/png':
      extn = 'png';
      break;
    case 'image/gif':
      extn = 'gif';
      break;
    case 'application/msword':
      extn = 'doc';
      break;
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      extn = 'docx';
      break;
    case 'application/vnd.ms-excel':
    case 'application/octet-stream':
      extn = 'xls';
      break;
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      extn = 'xlsx';
      break;
    case 'application/pdf':
      extn = 'pdf';
      break;
    case 'text/plain':
      extn = 'txt';
      break;
    default:
      extn = '';
  }
  return extn;
};

const fetchFromUrl = url =>
  fetch(url, {}).then(response => {
    if (response.ok) {
      return response.json();
    }
    return undefined;
  });

const isPackageTypeNotEqualsToBulk = packageType => packageType.toLowerCase() !== BULK;

const translateText = (textToTranslate = '', translationList = []) => {
  if (textToTranslate && translationList.length) {
    const translatedText = translationList.find(item => item.value.toLowerCase() === textToTranslate.toLowerCase());
    if (translatedText && translatedText.label) {
      return translatedText.label;
    }
  }
  return textToTranslate;
};

const getActionableNotifications = (notifications = [], groups = [], readCount = false) => {
  if (notifications.length && groups.length) {
    return readCount
      ? notifications.filter(item => checkForNotificationPermission(item.messageTitle, groups) && item.read === 0)
      : notifications.filter(item => checkForNotificationPermission(item.messageTitle, groups));
  }
  return [];
};

const getSalesAreaMap = (() => {
  let map;
  return () => {
    if (!map) {
      map = new Map();
      const salesAreaDetails = getAppConfig('mappedSalesAreaDetails');
      if (Array.isArray(salesAreaDetails)) {
        salesAreaDetails.forEach(salesAreaDetail => {
          salesAreaDetail.salesAreas.forEach(salesArea => {
            if (!map.get(salesArea)) {
              map.set(salesArea, salesAreaDetail.flow);
            }
          });
        });
      }
    }
    return map;
  };
})();

const getFlowBasedOnSalesArea = salesArea => getSalesAreaMap().get(salesArea) || '';

const restoreOriginalUri = async (_oktaAuth, originalUri) => {
  const { host = '', path = '' } = getAppConfig('server');
  const { redirectAfterLogin = '' } = getAppConfig('login');
  let baseUrlFromSettings = '';
  let redirect = '';

  if (!window.location.pathname.includes('/food/onboarding')) {
    const { baseURL } = await getUserInfo(originalUri);
    baseUrlFromSettings = baseURL;
    redirect = baseUrlFromSettings || `${path}${redirectAfterLogin}`;
  } else {
    redirect = originalUri && originalUri !== window.location.origin ? originalUri : `${path}${redirectAfterLogin}`;
    updateLocalStorage.removeItem(BASED_URL_KEY);
  }

  window.location.href = DOMPurify.sanitize(`${host}${redirect}`);
};

const handleErrorImage = (e, defaultImageUrl) => {
  const updateSrcSet = () => {
    const srcSet = e.target.srcset.split(',');

    return srcSet
      .map(set => {
        const [, dimension] = set.split(' ').filter(elem => elem.length);

        return `${defaultImageUrl} ${dimension}`;
      })
      .join();
  };
  e.target.onerror = null;
  e.target.src = DOMPurify.sanitize(defaultImageUrl);
  e.target.srcset = updateSrcSet();
};

const getApiUrls = (urlsObj = {}, isMockServer = false) =>
  Object.entries(urlsObj).reduce((result, [key, { url = '', mockUrl = '' }]) => {
    // Using mockUrl if isMockServer is true and mockUrl is provided
    const resolvedUrl = isMockServer && mockUrl ? mockUrl : url;
    // Building a new result object without mutating the existing one
    return { ...result, [key]: resolvedUrl };
  }, {});

export {
  blobToFile,
  cacheAccountAndRegion,
  checkIfUrlInlcudesCtaParameteres,
  convertArrToObj,
  debounce,
  deleteRedcarpetCookies,
  detectIE,
  falsyReturn,
  fetchFromUrl,
  getAccountAndRegion,
  getActionableNotifications,
  getApiUrls,
  getBaseUrl,
  getCachedUserContext,
  getCachedUserInfo,
  getCachedUserOktaInfo,
  getCurrentDate,
  getEmail,
  getFileExtension,
  getFlowBasedOnSalesArea,
  getUserInfo,
  getUserInfoFromOkta,
  getUsername,
  handleErrorImage,
  handleLogout,
  isCCCNA,
  isEmpty,
  isMobileDevice,
  isPackageTypeNotEqualsToBulk,
  isScrollToBelow,
  isValidPhoneFormat,
  PHONE_REGEX,
  redirectTo,
  restoreOriginalUri,
  scrollBelow,
  scrollBy,
  scrollToTop,
  scrollToTopAppContent,
  selectBoxOptions,
  srcSetToArray,
  throttle,
  translate,
  translateText,
  updateLocalStorage,
  updateSessionStorage
};
